import {
  drawVariant,
  updateVis,
  getColorMap,
  getSVDuplicationMesh,
  getSVDeletionMesh,
  getInsertionMesh,
  getBreakendMesh,
  getInversionMesh,
  getTandemDupMesh,
  getRepExpMesh,
} from "./cnvBlueprint";
import ClinicalSampleCnv from "../../../../models/ClinicalSampleCnv";
import {
  INVERSION,
  BREAKEND,
  DUPLICATION,
  INSERTION,
  DELETION,
  TANDEM_DUP,
  REPEAT_EXPANSION,
} from "../../constants/structuralVariants";

import packLargeRegions from "../../helpers/packRegions/packLargeRegions";
import getFilters from "./getFilters";
import Filter from "../../../../models/Filter";
import { pathogenicityIndex } from "../../constants/pathogenicity";
import TrackerArea from "../../../../models/TrackerArea";

const basePadding = 100;

function getDefaultState(otherSamples) {
  return {
    config: {},
    allVariants: [],
    allDuplications: [],
    allTandemDups: [],
    allDeletions: [],
    allInsertions: [],
    allInversions: [],
    allBreakEnds: [],
    allRepExpansions: [],
    floorHeight: 16,
    variantDict: new Map(),
    floors: [],
    filters: getFilters(otherSamples),
    allAnalyses: new Set(),
    allAnalysesDict: new Map(),
    analysisDict: new Map(),
  };
}

function getVariantsTrackController(track) {
  const { sample: currentSample, allSamples } = track?.conf?.data || {};
  const otherSamples = allSamples.filter((x) => x.name !== currentSample.name);
  const svDupMesh = getSVDuplicationMesh();
  const svDelMesh = getSVDeletionMesh();
  const tandemDupMesh = getTandemDupMesh();
  const insertionMesh = getInsertionMesh();
  const breakendMesh = getBreakendMesh();
  const inversionMesh = getInversionMesh();
  const repExpMesh = getRepExpMesh();

  let state = getDefaultState(otherSamples);

  function onReset() {
    state = getDefaultState(otherSamples);
  }

  function onInit() {
    track.setFilters(state.filters);
  }

  function onRangeChange({ chrSegments }) {
    const { analysisId } = track.getSharedState();

    const fullChrs = chrSegments.map((x) => ({
      ...x,
      ...x.chr.region,
    }));

    track.loadPosition(fullChrs, { analysisId });
  }

  function onFiltersChange(updated) {
    state.floors = [];
    state.filters = updated;
    track.setFilters([...state.filters]);
    setTimeout(() => {
      const {
        allVariants,
        allDeletions,
        allDuplications,
        allTandemDups,
        allBreakEnds,
        allRepExpansions,
        allInsertions,
        allInversions,
      } = state;

      const activeFilters = getActiveFilters();
      const hasFilters = Boolean(activeFilters.length);
      if (hasFilters) {
        applyFiltersToVariants(allVariants, activeFilters);
      } else {
        resetVariantsVisibility();
      }
      calcFilterCounts();
      packLargeRegions(allVariants, state.floors, basePadding);
      updateVis(allDuplications, svDupMesh);
      updateVis(allDeletions, svDelMesh);
      updateVis(allInsertions, insertionMesh);
      updateVis(allTandemDups, tandemDupMesh);
      updateVis(allInversions, inversionMesh);
      updateVis(allBreakEnds, breakendMesh);
      updateVis(allRepExpansions, repExpMesh);

      update();
    }, 100);
  }

  function getActiveFilters() {
    return state.filters.filter((x) => x.value);
  }

  function updateOccupiedAreas(cnvs, chr) {
    const sample = currentSample?.name;
    const areaTracker = new TrackerArea();
    const id = `${chr.name}-${sample}`;
    const createdAt = new Date().getTime();
    const newTracker = { sample, chr, id, areaTracker, createdAt };
    const existing = track.getSharedState()?.cnvAreas || [];

    cnvs.forEach((x) => areaTracker.addAreaNoMerge(x.region.g0, x.region.g1));
    areaTracker.mergeAll();

    const cnvAreas = existing.filter((x) => x.id !== id).concat([newTracker]);
    track.setSharedState({ cnvAreas });
  }

  function onDataReceived({ res, chr }) {
    const cnvs = processVariants(res, chr);
    if (cnvs.length === 0) return;

    updateOccupiedAreas(cnvs, chr);

    const allNew = cnvs.sort((a, b) => b.region.size - a.region.size);

    const newDups = allNew.filter((x) => x.type === DUPLICATION);
    const newDels = allNew.filter((x) => x.type === DELETION);
    const newInsertions = allNew.filter((x) => x.type === INSERTION);
    const newTandemDups = allNew.filter((x) => x.type === TANDEM_DUP);
    const newBreakends = allNew.filter((x) => x.type === BREAKEND);
    const newInversions = allNew.filter((x) => x.type === INVERSION);
    const newRepExps = allNew.filter((x) => x.type === REPEAT_EXPANSION);

    const activeFilters = getActiveFilters();
    state.allVariants = state.allVariants.concat(allNew);
    state.allDuplications = state.allDuplications.concat(newDups);
    state.allDeletions = state.allDeletions.concat(newDels);
    state.allInsertions = state.allInsertions.concat(newInsertions);
    state.allInversions = state.allInversions.concat(newInversions);
    state.allTandemDups = state.allTandemDups.concat(newTandemDups);
    state.allBreakEnds = state.allBreakEnds.concat(newBreakends);
    state.allRepExpansions = state.allRepExpansions.concat(newRepExps);

    svDupMesh.setItems(state.allDuplications);
    svDelMesh.setItems(state.allDeletions);
    insertionMesh.setItems(state.allInsertions);
    tandemDupMesh.setItems(state.allTandemDups);
    breakendMesh.setItems(state.allBreakEnds);
    inversionMesh.setItems(state.allInversions);
    repExpMesh.setItems(state.allRepExpansions);

    applyFiltersToVariants(allNew, activeFilters);
    packLargeRegions(allNew, state.floors, basePadding);
    drawVariant(newDups, svDupMesh);
    drawVariant(newDels, svDelMesh);
    drawVariant(newInsertions, insertionMesh);
    drawVariant(newTandemDups, tandemDupMesh);
    drawVariant(newInversions, inversionMesh);
    drawVariant(newBreakends, breakendMesh);
    drawVariant(newRepExps, repExpMesh);

    calcFilterCounts();
    update();
  }

  function applyFiltersToVariants(items, activeFilters) {
    const pathoDict = activeFilters
      .filter((x) => x.category === "Pathogenicity")
      .map((x) => x.key.split("-")[1])
      .map((x) => Number(x))
      .reduce((a, i) => a.set(i, true), new Map());

    const typeDict = activeFilters
      .filter((x) => x.category === "Type")
      .map((x) => x.key)
      .reduce((a, i) => a.set(i, true), new Map());

    const selectedAnalyses = activeFilters
      .filter((x) => x.category === "Analyses")
      .map((x) => x.key);

    const qualDict = activeFilters
      .filter((x) => x.category === "Quality")
      .map((x) => x.key.split("-")[1])
      .map((x) => Number(x))
      .reduce((a, i) => a.set(i, true), new Map());

    const samplesAny = activeFilters
      .filter((x) => x.category === "CNV In Any other Sample")
      .map((x) => x.key.replace(/^any-sample-/, ""));

    const samplesAll = activeFilters
      .filter((x) => x.category === "CNV In All other Samples")
      .map((x) => x.key.replace(/^all-sample-/, ""));

    const samplesNot = activeFilters
      .filter((x) => x.category === "CNV Not in Sample")
      .map((x) => x.key.replace(/^not-sample-/, ""));

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const patho = item.pathoIndex;
      const hidePatho = pathoDict.size && !pathoDict.get(patho);
      const hideType = typeDict.size && !typeDict.get(item.type);
      const hideQuality = qualDict.size && !qualDict.get(item.qualityIndex);
      const hideAnal =
        selectedAnalyses.length &&
        !selectedAnalyses.find((x) => item.inAnalyses.has(x));
      const hideAny =
        samplesAny.length && !samplesAny.find((x) => item.inSamples.has(x));
      const hideAll =
        samplesAll.length &&
        !samplesAll.reduce((a, x) => a && item.inSamples.has(x), true);
      const hideNone =
        samplesNot && samplesNot.find((x) => item.inSamples.has(x));

      item.hidden =
        hidePatho ||
        hideType ||
        hideAnal ||
        hideQuality ||
        hideAny ||
        hideAll ||
        hideNone;
    }
  }

  function resetVariantsVisibility() {
    const items = state.allVariants;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      item.hidden = false;
    }
  }

  function handleNewAnalysis(analId, createdAt) {
    const isPresent = state.allAnalyses.has(analId);
    if (!isPresent) {
      state.allAnalyses.add(analId);
      state.analysisDict.set(analId, createdAt);

      state.filters = [
        ...state.filters,
        new Filter({
          category: "Analyses",
          key: analId,
          value: 0,
          isVisible: true,
          totalCount: 0,
          label: `${createdAt.toLocaleString()} ${
            analId === window?.resultsConfig?.analysis_id ? " [current]" : ""
          }`,
        }),
      ];
    }
  }

  function processVariants(response, chr) {
    const res = response || {};
    const sampleRes = res?.cnvs?.[currentSample?.name] || {};
    const cnvs = sampleRes.cnvs || [];
    const analyses = sampleRes.analyses || [];
    const newCnvs = cnvs.map((x) => new ClinicalSampleCnv(x, chr));

    analyses.forEach((x) => handleNewAnalysis(x.id, x.createdAt));
    newCnvs.forEach((x) => state.variantDict.set(x.id, x));

    Array.from(response.analysisDict.keys()).forEach((x) => {
      state.allAnalysesDict.set(x, response.analysisDict.get(x));
    });

    return newCnvs;
  }

  function calcFilterCounts() {
    const countDict = state.filters
      .map(({ key }) => key)
      .reduce((a, i) => ({ ...a, [i]: 0 }), {});

    const pathoDict = Object.values(pathogenicityIndex)
      .map((x) => ({ key: x, val: `pathogenicity-${x}` }))
      .reduce((a, i) => ({ ...a, [i.key]: i.val }), {});

    const analyses = Array.from(state.allAnalyses.values());

    for (let i = 0; i < state.allVariants.length; i++) {
      const x = state.allVariants[i];
      const patho = pathoDict[x.pathoIndex];
      const passesAll = x.qualityIndex === 1 ? 1 : 0;
      const passesSome = x.qualityIndex === 2 ? 1 : 0;
      const failsAll = x.qualityIndex === 3 ? 1 : 0;

      countDict[patho] += 1;
      countDict[x.type] += 1;
      countDict[x.analysisId] += 1;
      countDict["quality-1"] += passesAll;
      countDict["quality-2"] += passesSome;
      countDict["quality-3"] += failsAll;

      for (let y = 0; y < otherSamples.length; y++) {
        const { name } = otherSamples[y];
        const filterKeyAny = `any-sample-${name}`;
        const filterKeyAll = `all-sample-${name}`;
        const filterKeyNot = `not-sample-${name}`;
        const isIn = x.inSamples.has(name) ? 1 : 0;
        const isOut = 1 - isIn;

        countDict[filterKeyAny] += isIn ? 1 : 0;
        countDict[filterKeyAll] += isIn ? 1 : 0;
        countDict[filterKeyNot] += isOut ? 0 : 1;
      }

      for (let y = 0; y < analyses.length; y++) {
        const a = analyses[y];
        countDict[a] += x.inAnalyses.has(a) ? 1 : 0;
      }
    }

    state.filters.forEach((x) => x.setTotalCount(countDict[x.key]));
    track.setFilters([...state.filters]);
  }

  function update() {
    const maxFloor = state.floors.length || 0;
    svDupMesh.uniforms.setMaxY(maxFloor);
    svDelMesh.uniforms.setMaxY(maxFloor);
    inversionMesh.uniforms.setMaxY(maxFloor);
    breakendMesh.uniforms.setMaxY(maxFloor);
    insertionMesh.uniforms.setMaxY(maxFloor);
    tandemDupMesh.uniforms.setMaxY(maxFloor);
    repExpMesh.uniforms.setMaxY(maxFloor);

    track.setHeight(maxFloor * state.floorHeight);
    track.requestRender();
  }

  function onPreferencesChanged() {
    state.config = track.getPreferences();
    updateColorMap();
  }

  function updateColorMap(highlight) {
    const { config } = state;
    const colormap = getColorMap(config, highlight);
    const noHighlightColorMap = getColorMap(config);
    const vectors = Array.from(colormap.values()).map((x) => x?.color?.vec);

    svDupMesh.uniforms.setColorMap(vectors);
    svDelMesh.uniforms.setColorMap(vectors);
    inversionMesh.uniforms.setColorMap(vectors);
    breakendMesh.uniforms.setColorMap(vectors);
    tandemDupMesh.uniforms.setColorMap(vectors);
    insertionMesh.uniforms.setColorMap(vectors);
    repExpMesh.uniforms.setColorMap(vectors);

    state.filters.forEach((x) => {
      x.setColor(noHighlightColorMap.get(x.key)?.color?.hex);
    });
    update();
  }

  function getState() {
    return state;
  }

  return {
    onInit,
    onReset,
    getState,
    onFiltersChange,
    onRangeChange,
    onDataReceived,
    onPreferencesChanged,
    meshes: [
      svDupMesh,
      svDelMesh,
      tandemDupMesh,
      insertionMesh,
      breakendMesh,
      inversionMesh,
      repExpMesh,
    ],
  };
}

export default getVariantsTrackController;
